import api from './client';
import { OBDDevice, OBDDeviceType, AvailableOBDDevice, DeviceConfig } from '../types/obd';

interface OBDDeviceResponse {
  success: boolean;
  data: OBDDevice;
}

interface OBDDeviceListResponse {
  success: boolean;
  data: { devices: OBDDevice[]; count: number };
}

interface AvailableDeviceListResponse {
  success: boolean;
  data: { devices: AvailableOBDDevice[]; count: number };
}

export const obdApi = {
  /**
   * List full device inventory — super_admin only.
   * Returns device_id, device_type, status, assigned_vehicle, createdAt.
   */
  async getDeviceInventory(): Promise<OBDDevice[]> {
    const response = await api.get<OBDDeviceListResponse>('/api/obd/devices/inventory');
    return response.data.data.devices;
  },

  /**
   * Create a new device in the inventory — super_admin only.
   */
  async createDevice(device_id: string, device_type: OBDDeviceType): Promise<OBDDevice> {
    const response = await api.post<OBDDeviceResponse>('/api/obd/devices/inventory', {
      device_id,
      device_type,
    });
    return response.data.data;
  },

  /**
   * List devices that are available to be assigned — company_admin+.
   * device_type is intentionally not returned by this endpoint.
   */
  async getAvailableDevices(companyId?: string): Promise<AvailableOBDDevice[]> {
    const params = companyId ? { company_id: companyId } : {};
    const response = await api.get<AvailableDeviceListResponse>('/api/obd/devices/available', { params });
    return response.data.data.devices;
  },

  /**
   * Assign a device to a vehicle.
   */
  async assignDevice(deviceId: string, vehicle_id: string): Promise<OBDDevice> {
    const response = await api.patch<OBDDeviceResponse>(
      `/api/obd/devices/${deviceId}/assign`,
      { vehicle_id }
    );
    return response.data.data;
  },

  /**
   * Unassign a device from its current vehicle.
   */
  async unassignDevice(deviceId: string): Promise<OBDDevice> {
    const response = await api.patch<OBDDeviceResponse>(
      `/api/obd/devices/${deviceId}/unassign`,
      {}
    );
    return response.data.data;
  },

  /**
   * Update device_id and/or device_type for an inventory device — super_admin only.
   * deviceId is the current IMEI.
   */
  async updateInventoryDevice(
    deviceId: string,
    updates: { device_id?: string; device_type?: OBDDeviceType }
  ): Promise<OBDDevice> {
    const response = await api.patch<OBDDeviceResponse>(
      `/api/obd/devices/inventory/${deviceId}`,
      updates
    );
    return response.data.data;
  },

  /**
   * Delete a device from the inventory — super_admin only.
   * Will fail with 409 if the device is currently assigned.
   */
  async deleteInventoryDevice(deviceId: string): Promise<void> {
    await api.delete(`/api/obd/devices/inventory/${deviceId}`);
  },

  /**
   * Fetch the configuration for a specific inventory device — super_admin only.
   * Returns server/auth settings and AVL IO element list for Teltonika configurator.
   */
  async getDeviceConfig(deviceId: string): Promise<DeviceConfig> {
    const response = await api.get<{ success: boolean; data: DeviceConfig }>(
      `/api/obd/devices/inventory/${deviceId}/config`
    );
    return response.data.data;
  },

  /**
   * Regenerate the API key for a device — super_admin only.
   * The old key is immediately revoked. Returns the full config with the new key.
   */
  async regenerateApiKey(deviceId: string): Promise<DeviceConfig> {
    const response = await api.post<{ success: boolean; data: DeviceConfig }>(
      `/api/obd/devices/inventory/${deviceId}/regenerate-key`
    );
    return response.data.data;
  },

  /**
   * Allocate a device to a company — super_admin only.
   * deviceId is the IMEI. Supports initial allocation and reassignment.
   */
  async allocateDevice(deviceId: string, company_id: string): Promise<OBDDevice> {
    const response = await api.patch<OBDDeviceResponse>(
      `/api/obd/devices/inventory/${deviceId}/allocate`,
      { company_id }
    );
    return response.data.data;
  },
};
